/**
 * A user in a workspace, as the app really knows them — and the rules for who
 * may change whom.
 *
 * Deliberately only what exists: a name, an email, a role, when they joined (or
 * were invited), and whether they have accepted yet. The old People screen also
 * showed phone, department, location and online status — none of which any
 * real account has.
 */
import { z } from 'zod'
import { assignableRoles, can, isAssignable, type Role, roles } from '../access/index.ts'
import { INVITATION_RESEND_COOLDOWN_SECONDS } from './invitation.ts'
import { personNameSchema } from './name.ts'

/**
 * An invited user is listed before they accept, so admins can see who is
 * pending. `deactivated` is a joined member an Admin has signed out and
 * locked (ADR single-organization addendum 2026-09-25) — their role is untouched underneath.
 */
export const userStatuses = ['active', 'invited', 'deactivated'] as const
export type UserStatus = (typeof userStatuses)[number]

export const workspaceUserSchema = z.object({
  /** The membership id, or the invitation id while still invited. */
  id: z.string().min(1),
  /** The account behind a membership. Null while still only an invitation. */
  userId: z.string().nullable(),
  /** "First Last", derived from the two below (name.ts). An invitation shows its email here. */
  name: z.string().min(1),
  /** Empty while still only an invitation, and for an old account with a one-word name. */
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  role: z.enum(roles),
  status: z.enum(userStatuses),
  /** When they joined, or when they were invited. */
  joinedAt: z.iso.datetime(),
  /**
   * An email change an admin asked for that the person hasn't confirmed yet.
   * The address above stays theirs until they click the link sent here.
   */
  pendingEmail: z.email().nullable().default(null),
  /** When the invitation's CURRENT link was made. Invitations only. */
  invitationSentAt: z.iso.datetime().nullable().default(null),
  /** When the invitation's CURRENT link stops working. Invitations only. */
  invitationExpiresAt: z.iso.datetime().nullable().default(null),
  /** When an Admin deactivated them. Members only. */
  deactivatedAt: z.iso.datetime().nullable().default(null),
  /**
   * When this membership last made an authenticated request — null if never
   * (including every invitation: it has no membership yet). Members only;
   * an admin impersonating someone never updates the person they're
   * impersonating (docs/DECISIONS.md D-013, docs/IMPERSONATION.md).
   */
  lastActiveAt: z.iso.datetime().nullable().default(null),
})

export type WorkspaceUser = z.infer<typeof workspaceUserSchema>

/** What an admin can edit about someone. Role has its own control. */
export const updateUserDetailsSchema = personNameSchema.extend({
  email: z.email('Enter a valid email address.').transform((email) => email.trim().toLowerCase()),
})
export type UpdateUserDetailsInput = z.infer<typeof updateUserDetailsSchema>

/** How long the confirmation link for an email change works. */
export const EMAIL_CHANGE_LIFETIME_HOURS = 48

export const inviteUserSchema = z.object({
  email: z.email('Enter a valid email address.').transform((email) => email.trim().toLowerCase()),
  role: z.enum(assignableRoles).default('staff'),
})
export type InviteUserInput = z.infer<typeof inviteUserSchema>

export const userStatusLabels: Record<UserStatus, string> = {
  active: 'Active',
  invited: 'Invited',
  deactivated: 'Deactivated',
}

/** What a signed-in, deactivated person is told, wherever they try. */
export const DEACTIVATED_MESSAGE = 'This account has been deactivated. Ask an Admin.'

/**
 * The ONE rule for "who counts as present" — who to list for a choice (assign,
 * email, impersonate) or count as an active admin (D-011). A deactivated
 * person, or one waiting as Requested, is neither.
 */
export function isActiveMember(u: WorkspaceUser): boolean {
  return u.status === 'active' && isAssignable(u.role)
}

/** Has this invitation's current link run out? Invitations only. */
export function isInvitationExpired(u: WorkspaceUser, now = new Date()): boolean {
  if (!u.invitationExpiresAt) return false
  return new Date(u.invitationExpiresAt).getTime() <= now.getTime()
}

/**
 * Whole seconds until this invitation can be resent again — 0 when it's
 * allowed right now, or when it was never sent (no `invitationSentAt`).
 */
export function resendAvailableIn(u: WorkspaceUser, now = new Date()): number {
  if (!u.invitationSentAt) return 0
  const elapsedMs = now.getTime() - new Date(u.invitationSentAt).getTime()
  const remainingMs = INVITATION_RESEND_COOLDOWN_SECONDS * 1000 - elapsedMs
  return remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0
}

/**
 * People waiting to be let in first — they are what an admin opens this page
 * to act on — then admins, then staff; invitations after everyone, and
 * deactivated people after invitations.
 */
export function sortUsers(users: readonly WorkspaceUser[]): WorkspaceUser[] {
  const rank = (u: WorkspaceUser) =>
    u.role === 'requested'
      ? -1
      : (u.status === 'deactivated' ? 20 : u.status === 'invited' ? 10 : 0) + roles.indexOf(u.role)
  return [...users].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
}

// ─── Who may change whom ────────────────────────────────────────────────────
//
// Pure, so the real database and Test mode apply exactly the same rules, and
// the rules can be tested without a server.

/** The person making the change. */
export interface Actor {
  userId: string
  role: Role
}

export type Verdict = { ok: true } | { ok: false; reason: string }
const allow: Verdict = { ok: true }
const deny = (reason: string): Verdict => ({ ok: false, reason })

export function canManageUsers(actor: Actor): boolean {
  return can(actor.role, 'users.manage')
}

// ─── The last active Admin ──────────────────────────────────────────────────

/**
 * One membership, as the last-Admin rule needs it. The repository reads these
 * fresh, under the organization's lock, just before it changes anything — so
 * the rule is applied to what is true at that moment, not to a list fetched
 * a moment earlier.
 */
export interface MembershipFacts {
  id: string
  role: Role
  active: boolean
}

/** Joined people as membership facts — invitations have no membership yet. */
export function membershipFacts(users: readonly WorkspaceUser[]): MembershipFacts[] {
  return users
    .filter((u) => u.status !== 'invited')
    .map((u) => ({ id: u.id, role: u.role, active: u.status === 'active' }))
}

/**
 * Would this change leave the organization with no active Admin? `next` is
 * what the target becomes — its role and whether it is active — or null when
 * it is removed. The ONE rule behind changing a role, removing and
 * deactivating (docs/PERMISSIONS.md), used by the screen, the router's
 * check and the repository's re-check under the lock alike.
 */
export function leavesNoActiveAdmin(
  members: readonly MembershipFacts[],
  targetId: string,
  next: { role: Role; active: boolean } | null,
): boolean {
  const isActiveAdmin = (m: MembershipFacts) => m.active && m.role === 'admin'
  const before = members.filter(isActiveAdmin).length
  const after = members.flatMap((m) => (m.id !== targetId ? [m] : next ? [{ ...m, ...next }] : []))
  return before > 0 && after.filter(isActiveAdmin).length === 0
}

export function checkInvite(
  actor: Actor,
  input: { email: string; role: Role },
  users: readonly WorkspaceUser[],
): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can invite people.')
  const existing = users.find((u) => u.email.toLowerCase() === input.email.toLowerCase())
  if (existing?.role === 'requested')
    return deny(`${input.email} has already asked to join — choose their role in the list instead.`)
  if (existing?.status === 'active') return deny(`${input.email} is already in this workspace.`)
  if (existing?.status === 'deactivated')
    return deny(`${input.email} is deactivated — reactivate them instead.`)
  if (existing?.status === 'invited')
    return deny(`${input.email} already has an invitation — resend it instead.`)
  return allow
}

export function checkRoleChange(
  actor: Actor,
  target: WorkspaceUser,
  nextRole: Role,
  users: readonly WorkspaceUser[],
): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can change roles.')
  if (target.status === 'deactivated') return deny('Reactivate them first to change their role.')
  if (target.role === nextRole) return allow
  if (!isAssignable(nextRole)) return deny('Choose Admin or Staff.')
  if (leavesNoActiveAdmin(membershipFacts(users), target.id, { role: nextRole, active: true })) {
    return deny('A workspace needs at least one admin. Make someone else an admin first.')
  }
  return allow
}

export function checkRemove(
  actor: Actor,
  target: WorkspaceUser,
  users: readonly WorkspaceUser[],
): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can remove people.')
  if (target.userId !== null && target.userId === actor.userId) {
    return deny('You cannot remove yourself from the workspace.')
  }
  if (leavesNoActiveAdmin(membershipFacts(users), target.id, null)) {
    return deny('A workspace needs at least one admin.')
  }
  return allow
}

/**
 * Editing someone's name or email. Admins only, and only people who have
 * joined — an invitation has no account to edit; withdraw it and invite again.
 */
export function checkEditDetails(actor: Actor, target: WorkspaceUser): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can edit people’s details.')
  if (target.status === 'deactivated') return deny('Reactivate them to edit their details.')
  if (target.userId === null || target.status !== 'active')
    return deny('An invitation has no account to edit yet — withdraw it and invite again.')
  return allow
}

/**
 * Sign someone out and lock them out, leaving their role untouched
 * (ADR single-organization addendum 2026-09-25, D-007).
 */
export function checkDeactivate(
  actor: Actor,
  target: WorkspaceUser,
  users: readonly WorkspaceUser[],
): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can deactivate people.')
  if (target.status !== 'active' || !isAssignable(target.role))
    return deny('Only someone who has joined can be deactivated.')
  if (target.userId !== null && target.userId === actor.userId) {
    return deny('You can’t deactivate yourself.')
  }
  if (
    leavesNoActiveAdmin(membershipFacts(users), target.id, { role: target.role, active: false })
  ) {
    return deny('A workspace needs at least one active admin. Make someone else an admin first.')
  }
  return allow
}

/** Give back exactly the role they had — the role column was never touched. */
export function checkReactivate(actor: Actor, target: WorkspaceUser): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can reactivate people.')
  if (target.status !== 'deactivated') return deny('They aren’t deactivated.')
  return allow
}

/**
 * Send a waiting invitation's link again — expired is the main reason to
 * (D-006). Rate-limited to one a minute per invitation, as a race backstop:
 * the router pre-checks this; the repository re-checks under a lock.
 */
export function checkResendInvitation(
  actor: Actor,
  target: WorkspaceUser,
  now = new Date(),
): Verdict {
  if (!canManageUsers(actor)) return deny('Only admins can resend invitations.')
  if (target.status !== 'invited') return deny('Only an invitation can be resent.')
  const remaining = resendAvailableIn(target, now)
  if (remaining > 0) return deny(`Sent a moment ago — you can resend in ${remaining}s.`)
  return allow
}
